import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Plus, RefreshCw, Trash2, Unlock, Unplug } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

export default function Ftth() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "aktif";
  const [active, setActive] = useState([]);
  const [secrets, setSecrets] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState({ name: "", password: "", service: "pppoe", profile: "default", local_address: "", remote_address: "" });
  const [profile, setProfile] = useState({ name: "", rate_limit: "", local_address: "", remote_address: "" });
  const [loading, setLoading] = useState(true);
  const [qActive, setQActive] = useState("");
  const [qInactive, setQInactive] = useState("");

  const loadAll = async () => {
    try {
      const [a, s, p] = await Promise.all([api.get("/ppp/active"), api.get("/ppp/secrets"), api.get("/ppp/profiles")]);
      setActive(a.data); setSecrets(s.data); setProfiles(p.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); const t = setInterval(loadAll, 20000); return () => clearInterval(t); }, []);

  const addSecret = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/ppp/secrets", secret); toast.success(`Secret ${secret.name} dibuat`); setSecret({ name: "", password: "", service: "pppoe", profile: "default", local_address: "", remote_address: "" }); loadAll(); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const addProfile = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/ppp/profiles", profile); toast.success(`Profil ${profile.name} dibuat`); setProfile({ name: "", rate_limit: "", local_address: "", remote_address: "" }); loadAll(); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const kick = async (a) => {
    try { await api.delete(`/ppp/active/${encodeURIComponent(a.id)}`); toast.success(`${a.name} diputus`); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const delSecret = async (s) => {
    try { await api.delete(`/ppp/secrets/${encodeURIComponent(s.id)}`); toast.success("Secret dihapus"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const isolir = async (s) => {
    try { await api.post("/ppp/isolir", { id: s.id, name: s.name }); toast.success(`${s.name} diisolir & koneksi diputus`); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const restore = async (s) => {
    try { const { data } = await api.post("/ppp/restore", { id: s.id, name: s.name }); toast.success(`${s.name} aktif kembali (profil ${data.profile})`); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const isolated = secrets.filter((s) => s.isolated);
  const inactive = secrets.filter((s) => !s.online);

  const match = (obj, q, keys) =>
    keys.some((k) => String(obj[k] ?? "").toLowerCase().includes(q));
  const qa = qActive.trim().toLowerCase();
  const qi = qInactive.trim().toLowerCase();
  const activeShown = qa ? active.filter((a) => match(a, qa, ["name", "address", "service", "uptime", "caller_id"])) : active;
  const inactiveShown = qi ? inactive.filter((s) => match(s, qi, ["name", "profile", "service", "comment"])) : inactive;

  return (
    <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["aktif", "PPPoE Aktif"], ["inaktif", "PPPoE Inaktif"], ["secret", "PPPoE Secret"], ["profil", "Profil"], ["isolir", "Isolir"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="aktif">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Cari secret PPPoE / IP / service…" value={qActive} onChange={(e) => setQActive(e.target.value)}
              data-testid="ppp-active-search" className="h-10 w-[260px] rounded-full bg-secondary/50"
            />
            {qActive && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setQActive("")} data-testid="ppp-active-search-clear">Bersihkan</Button>}
            <p className="text-sm text-muted-foreground" data-testid="ppp-active-count">{activeShown.length} dari {active.length} koneksi PPPoE aktif</p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={loadAll} data-testid="refresh-ppp"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Muat ulang</Button>
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="ppp-active-table">
            <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>IP</TableHead><TableHead>Uptime</TableHead><TableHead>Service</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {activeShown.map((a) => (
                <TableRow key={a.id} className="font-mono text-xs">
                  <TableCell data-label="Username">{a.name}</TableCell><TableCell data-label="IP">{a.address}</TableCell><TableCell data-label="Uptime">{a.uptime}</TableCell><TableCell data-label="Service">{a.service}</TableCell>
                  <TableCell data-label="" className="text-right cell-actions"><Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => kick(a)} data-testid={`ppp-kick-${a.name}`}><Unplug className="mr-1 h-3.5 w-3.5" /> Putuskan</Button></TableCell>
                </TableRow>
              ))}
              {!activeShown.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Mengambil data dari Mikrotik…" : qActive ? `Tidak ada koneksi yang cocok dengan "${qActive}"` : "Tidak ada koneksi aktif"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="inaktif">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Cari secret PPPoE / profil / service…" value={qInactive} onChange={(e) => setQInactive(e.target.value)}
            data-testid="ppp-inactive-search" className="h-10 w-[260px] rounded-full bg-secondary/50"
          />
          {qInactive && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setQInactive("")} data-testid="ppp-inactive-search-clear">Bersihkan</Button>}
          <p className="text-sm text-muted-foreground" data-testid="ppp-inactive-count">{inactiveShown.length} dari {inactive.length} secret offline</p>
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="ppp-inactive-table">
            <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>Profile</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {inactiveShown.map((s) => (
                <TableRow key={s.id} className="font-mono text-xs">
                  <TableCell data-label="Username">{s.name}</TableCell><TableCell data-label="Profile">{s.profile}</TableCell><TableCell data-label="Service">{s.service}</TableCell>
                  <TableCell data-label="Status"><span className="rounded-full border border-red-500/40 px-2 py-0.5 text-[10px] text-red-400">offline</span></TableCell>
                </TableRow>
              ))}
              {!inactiveShown.length && <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground cell-empty">{qInactive ? `Tidak ada secret offline yang cocok dengan "${qInactive}"` : "Semua secret sedang online"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="secret">
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={addSecret} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="secret-form">
            <h3 className="font-display text-lg font-semibold">Add Secret PPPoE</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Username" required value={secret.name} onChange={(e) => setSecret({ ...secret, name: e.target.value })} data-testid="secret-name" />
              <Field label="Password" required value={secret.password} onChange={(e) => setSecret({ ...secret, password: e.target.value })} data-testid="secret-password" />
              <Field label="Service" value={secret.service} onChange={(e) => setSecret({ ...secret, service: e.target.value })} data-testid="secret-service" />
              <Field label="Profile" list="ppp-profiles" value={secret.profile} onChange={(e) => setSecret({ ...secret, profile: e.target.value })} data-testid="secret-profile" />
              <Field label="Local Address" value={secret.local_address} onChange={(e) => setSecret({ ...secret, local_address: e.target.value })} data-testid="secret-local" />
              <Field label="Remote Address" value={secret.remote_address} onChange={(e) => setSecret({ ...secret, remote_address: e.target.value })} data-testid="secret-remote" />
            </div>
            <datalist id="ppp-profiles">{profiles.map((p) => <option key={p.name} value={p.name} />)}</datalist>
            <Button type="submit" disabled={busy} data-testid="secret-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Simpan</>}
            </Button>
          </form>

          <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
            <Table data-testid="secrets-table">
              <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>Profile</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
              <TableBody>
                {secrets.map((s) => (
                  <TableRow key={s.id} className="font-mono text-xs">
                    <TableCell data-label="Username">{s.name}</TableCell><TableCell data-label="Profile">{s.profile}</TableCell>
                    <TableCell data-label="Status">{s.isolated ? <span className="text-amber-400">isolir</span> : s.online ? <span className="text-emerald-400">online</span> : <span className="text-muted-foreground">offline</span>}</TableCell>
                    <TableCell data-label="" className="space-x-1 text-right cell-actions">
                      {s.isolated ? (
                        <Button size="sm" variant="outline" className="rounded-full text-emerald-400" onClick={() => restore(s)} data-testid={`restore-${s.name}`}><Unlock className="mr-1 h-3.5 w-3.5" /> Buka</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full text-amber-400" onClick={() => isolir(s)} data-testid={`isolir-${s.name}`}><Lock className="mr-1 h-3.5 w-3.5" /> Isolir</Button>
                      )}
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delSecret(s)} data-testid={`del-secret-${s.name}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="profil">
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={addProfile} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="profile-form">
            <h3 className="font-display text-lg font-semibold">Add Profil PPPoE</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nama Profil" required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" />
              <Field label="Rate Limit (mis. 10M/10M)" value={profile.rate_limit} onChange={(e) => setProfile({ ...profile, rate_limit: e.target.value })} data-testid="profile-rate" />
              <Field label="Local Address" value={profile.local_address} onChange={(e) => setProfile({ ...profile, local_address: e.target.value })} data-testid="profile-local" />
              <Field label="Remote Address / Pool" value={profile.remote_address} onChange={(e) => setProfile({ ...profile, remote_address: e.target.value })} data-testid="profile-remote" />
            </div>
            <Button type="submit" disabled={busy} data-testid="profile-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Simpan</>}
            </Button>
          </form>
          <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
            <Table data-testid="profiles-table">
              <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Rate Limit</TableHead><TableHead>Local</TableHead></TableRow></TableHeader>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.id || p.name} className="font-mono text-xs">
                    <TableCell data-label="Nama">{p.name}</TableCell><TableCell data-label="Rate Limit">{p.rate_limit || "-"}</TableCell><TableCell data-label="Local">{p.local_address || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="isolir">
        <div className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-300">
          Isolir memindahkan pelanggan ke profil isolir (bandwidth dibatasi) <b>dan</b> memutus koneksi aktifnya. Nama profil isolir diatur di Setting → Pengaturan Umum.
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="isolir-table">
            <TableHeader><TableRow><TableHead>Pelanggan</TableHead><TableHead>Profil Sekarang</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {secrets.map((s) => (
                <TableRow key={s.id} className="font-mono text-xs">
                  <TableCell data-label="Pelanggan">{s.name}</TableCell>
                  <TableCell data-label="Profil Sekarang">{s.profile}</TableCell>
                  <TableCell data-label="Status">{s.isolated ? <span className="rounded-full border border-amber-500/40 px-2 py-0.5 text-[10px] text-amber-400">isolir</span> : <span className="rounded-full border border-emerald-500/40 px-2 py-0.5 text-[10px] text-emerald-400">aktif</span>}</TableCell>
                  <TableCell data-label="" className="text-right cell-actions">
                    {s.isolated ? (
                      <Button size="sm" variant="outline" className="rounded-full text-emerald-400" onClick={() => restore(s)} data-testid={`isolir-tab-restore-${s.name}`}><Unlock className="mr-1 h-3.5 w-3.5" /> Buka Isolir</Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full text-amber-400" onClick={() => isolir(s)} data-testid={`isolir-tab-${s.name}`}><Lock className="mr-1 h-3.5 w-3.5" /> Isolir</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!secrets.length && <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Mengambil data dari Mikrotik…" : "Belum ada pelanggan PPPoE"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{isolated.length} pelanggan sedang diisolir.</p>
      </TabsContent>
    </Tabs>
  );
}
